// "Needs you" — the andons, quality alerts and todos waiting on this person.
//
// The app could already raise an issue and never showed it again. These rows
// are the other half of that promise: what happened after you called it in, who
// is coming, and whether they are late.
//
// An andon row leaves the app. Acknowledging and answering belong to the Andon
// app, which owns the clock behind them, so the row is marked as a link out
// rather than pretending the tap stays here.

import type { NeedsYouVM } from '@/api/types';
import { Icon } from '@/components/ui/Icon';
import { EmptyState, Skeleton } from '@/components/ui/misc';
import { useT } from '@/i18n/useT';
import { Dot, type Tone } from './Score';

const TONE: Record<NeedsYouVM['tone'], Tone> = {
  danger: 'danger',
  warn: 'warn',
  neutral: 'neutral',
};

export function NeedsYouList({
  items,
  isPending,
  compact = false,
  onOpen,
  emptyText,
}: {
  items: NeedsYouVM[];
  isPending?: boolean;
  /** One meta line instead of two, and tighter rows (frame 3b). */
  compact?: boolean;
  onOpen: (item: NeedsYouVM) => void;
  emptyText?: string;
}) {
  const t = useT();
  if (isPending && items.length === 0) {
    return (
      <div style={{ display: 'grid', gap: 6 }}>
        <Skeleton height={compact ? 44 : 56} radius={compact ? 12 : 14} />
        <Skeleton height={compact ? 44 : 56} radius={compact ? 12 : 14} />
      </div>
    );
  }
  if (items.length === 0) {
    return (
      <div style={{ background: 'var(--surface2)', borderRadius: 13 }}>
        <EmptyState>{emptyText ?? t.nothingWaitingOnYouT}</EmptyState>
      </div>
    );
  }
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: compact ? 6 : 8 }}>
      {items.map((item) => (
        <NeedsYouRow key={item.key} item={item} compact={compact} onOpen={onOpen} />
      ))}
    </div>
  );
}

export function NeedsYouRow({
  item,
  compact = false,
  onOpen,
}: {
  item: NeedsYouVM;
  compact?: boolean;
  onOpen: (item: NeedsYouVM) => void;
}) {
  return (
    <button
      type="button"
      className="press-row"
      onClick={() => onOpen(item)}
      style={{
        width: '100%',
        textAlign: 'left',
        background: 'var(--surface)',
        border: '1px solid var(--line)',
        borderRadius: compact ? 12 : 14,
        padding: compact ? '10px 12px' : '12px 14px',
        display: 'flex',
        alignItems: 'center',
        gap: compact ? 8 : 10,
        minHeight: 44,
      }}
    >
      <Dot tone={TONE[item.tone]} size={compact ? 8 : 9} />
      <span style={{ flex: 1, minWidth: 0 }}>
        <span
          style={{
            display: 'block',
            font: `700 ${compact ? 11.5 : 13}px Manrope,sans-serif`,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {item.ref ? `${item.ref} — ${item.title}` : item.title}
        </span>
        {item.meta && (
          <span
            style={{
              display: 'block',
              font: `500 ${compact ? 10 : 11}px Manrope,sans-serif`,
              color: 'var(--sub)',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {item.meta}
          </span>
        )}
      </span>
      <span aria-hidden="true" style={{ color: 'var(--sub)', display: 'flex' }}>
        <Icon name={item.href ? 'external' : 'chevron'} size={compact ? 13 : 15} />
      </span>
    </button>
  );
}
